// Heritex Innovation — product catalogue
// Each entry powers /products/<slug> via <Product /> template.

const SHARED_FAQ_BILLING = [
  {
    q: "Do I need to pay to try it?",
    a: "No. Every product has a Free tier so you can use the core modules on a single workspace before deciding to upgrade.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes — subscriptions are month-to-month with no lock-in. Annual plans get 2 months free.",
  },
  {
    q: "Will you migrate my existing data?",
    a: "On Growth and above we do a free guided migration. For Enterprise we run a full white-glove import including custom field mapping.",
  },
];

const SHARED_COMPLIANCE = [
  "DPDP 2023 ready",
  "256-bit AES at rest",
  "TLS 1.3 in transit",
  "Role-based access",
  "Audit logs",
  "Daily backups",
];

const SHARED_INTEGRATIONS = [
  "Razorpay",
  "Stripe",
  "WhatsApp",
  "Twilio",
  "Google Workspace",
  "Zapier",
];

const products = [
  /* ------------------------------------------------------------------ */
  {
    slug: "custom-crm",
    category: "CRM",
    brand: null,
    name: "Custom CRM Systems",
    tagline: "Sales, ops and customer pipelines around how your team actually works.",
    hero: {
      eyebrow: "CRM · Self-serve",
      image:
        "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1600&q=80",
      trustBadges: ["Used by 40+ teams", "Custom fields & pipelines", "5-min setup"],
    },
    outcomes: [
      { value: "3×", label: "Faster lead-to-close cycle" },
      { value: "0", label: "Spreadsheets needed" },
      { value: "10 min", label: "Avg. onboarding time" },
    ],
    personas: [
      { name: "Real Estate Teams", desc: "Site visits, units, brokers and follow-ups, in one pipeline." },
      { name: "Service Businesses", desc: "Quotes, jobs, recurring clients and SLAs." },
      { name: "B2B Sales Teams", desc: "Multi-stage pipelines, forecasts and deal velocity." },
      { name: "Education / Coaching", desc: "Admissions funnels, counsellor follow-ups, enrolments." },
    ],
    modules: [
      { name: "Custom Pipelines", desc: "Drag-and-drop deal stages tailored to your sales process." },
      { name: "Contacts & Companies", desc: "Unified records with full activity timeline." },
      { name: "Tasks & Reminders", desc: "Auto-assignment, SLAs, no lead falls through the cracks." },
      { name: "Email & WhatsApp", desc: "Two-way sync, templates, drip sequences." },
      { name: "Quotes & Invoices", desc: "Send, e-sign, get paid — without leaving CRM." },
      { name: "Reports & Forecasts", desc: "Pipeline health, win rates, revenue forecasts." },
    ],
    packs: [
      { name: "Real Estate CRM", tagline: "Built for builders, brokers and channel partners.", features: ["Inventory & units", "Site-visit scheduler", "Broker payouts", "WhatsApp broadcast"], price: "Free" },
      { name: "Sales Team CRM", tagline: "Classic B2B pipeline with forecasting.", features: ["Multi-stage pipeline", "Deal forecasting", "Email sequences", "Goal tracking"], price: "₹999/mo" },
      { name: "Education Admissions CRM", tagline: "From enquiry to enrolment.", features: ["Source tracking", "Counsellor assignment", "Course catalogue", "Payment links"], price: "₹1,499/mo" },
      { name: "Service Business CRM", tagline: "Jobs, recurring clients, SLAs.", features: ["Job scheduling", "Quotation builder", "Recurring billing", "Field-team app"], price: "₹1,499/mo" },
    ],
    howItWorks: ["Sign up free", "Pick a CRM pack", "Import contacts (CSV / Google)", "Invite team", "Start selling"],
    pricing: [
      { tier: "Free", price: "₹0", features: ["1 user", "Up to 100 contacts", "1 pipeline", "Heritex branding"] },
      { tier: "Growth", price: "₹999 / user / mo", popular: true, features: ["Unlimited contacts", "Email & WhatsApp sync", "Custom fields", "Reports"] },
      { tier: "Business", price: "₹2,499 / user / mo", features: ["Custom domain", "API access", "Workflow automations", "Priority support"] },
      { tier: "Enterprise", price: "Talk to us", features: ["SSO / SAML", "Dedicated server", "Custom modules", "99.95% SLA"] },
    ],
    integrations: SHARED_INTEGRATIONS,
    compliance: SHARED_COMPLIANCE,
    faqs: [
      { q: "Can I customise fields and stages?", a: "Yes — every pipeline, field and form is fully editable. No code." },
      { q: "Does it work on mobile?", a: "Yes, native iOS and Android apps included on every plan." },
      ...SHARED_FAQ_BILLING,
    ],
    related: ["automation", "analytics-dashboards", "ai-chatbots"],
  },

  /* ------------------------------------------------------------------ */
  {
    slug: "hospital-management",
    category: "Healthcare",
    brand: "TrataX One",
    name: "Hospital Management System",
    tagline: "A unified operating system for clinics, hospitals and diagnostic labs.",
    hero: {
      eyebrow: "Healthcare · Flagship",
      image:
        "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&w=1600&q=80",
      trustBadges: ["12+ hospitals live", "HIPAA-aligned", "99.9% uptime"],
    },
    outcomes: [
      { value: "60%", label: "Less time per patient discharge" },
      { value: "4 hrs", label: "Saved per doctor per day" },
      { value: "₹0", label: "Setup on Solo tier" },
    ],
    personas: [
      { name: "Solo Clinics", desc: "Single-doctor practices that need OPD + billing without overhead." },
      { name: "Multi-speciality Hospitals", desc: "Several departments, OPD + IPD, multi-floor billing." },
      { name: "Diagnostic Labs", desc: "Sample collection → tests → reports → e-delivery." },
      { name: "Dental & Wellness", desc: "Appointment-led practices with recurring patients." },
    ],
    modules: [
      { name: "Patient Records (EMR)", desc: "Registration, history, allergies, prescriptions, attachments." },
      { name: "OPD & IPD", desc: "Queue, doctor scheduling, bed management, discharge summaries." },
      { name: "Billing & Insurance", desc: "Itemised bills, GST, TPA / cashless flows." },
      { name: "Pharmacy", desc: "Inventory, batches, expiry alerts, indenting, sales." },
      { name: "Laboratory", desc: "Test catalogue, sample tracking, signed PDF reports." },
      { name: "Telemedicine", desc: "In-app video consults, e-prescriptions, payments." },
      { name: "Doctor App", desc: "iOS / Android — schedule, EMR, prescribe on the go." },
      { name: "Reports & Audit", desc: "Revenue, footfall, doctor performance, audit logs." },
    ],
    packs: [
      { name: "Solo Clinic", tagline: "Free forever for single-doctor practices.", features: ["OPD queue", "EMR", "Billing", "Prescriptions"], price: "Free" },
      { name: "Multi-speciality OPD", tagline: "Multiple doctors, single front-desk.", features: ["Doctor scheduling", "Centralised billing", "Patient portal", "WhatsApp reminders"], price: "₹3,999/mo" },
      { name: "Diagnostic Lab", tagline: "Sample → test → signed report.", features: ["Test catalogue", "Sample barcode", "Signed PDF reports", "Home-collection ops"], price: "₹2,999/mo" },
      { name: "Dental Practice", tagline: "Appointments, tooth-charting, recalls.", features: ["Tooth charting", "Treatment plans", "Recall reminders", "Tray-photo uploads"], price: "₹1,999/mo" },
      { name: "IPD + Pharmacy", tagline: "Beds, indents, discharges, pharmacy.", features: ["Bed management", "Indenting", "Pharmacy POS", "Discharge summaries"], price: "₹6,999/mo" },
      { name: "Telemedicine-only", tagline: "Run a fully online clinic.", features: ["Video consults", "Online payments", "E-prescriptions", "Follow-up reminders"], price: "₹1,499/mo" },
    ],
    howItWorks: ["Sign up free", "Pick a clinical pack", "Add doctors & rooms", "Go live in 10 minutes"],
    pricing: [
      { tier: "Free (Solo)", price: "₹0", features: ["1 doctor", "Up to 50 patients/mo", "Core OPD + billing"] },
      { tier: "Growth", price: "₹3,999 / mo", popular: true, features: ["Up to 5 doctors", "All clinical modules", "WhatsApp + email", "Custom domain"] },
      { tier: "Business", price: "₹9,999 / mo", features: ["Unlimited doctors", "IPD + Pharmacy", "TPA / Insurance flows", "Priority support"] },
      { tier: "Enterprise", price: "Talk to us", features: ["On-prem / private cloud", "HL7 / ABDM integration", "24×7 SLA", "Dedicated CSM"] },
    ],
    integrations: ["Razorpay", "WhatsApp", "Twilio", "Google Calendar", "Tally", "ABDM (Ayushman)"],
    compliance: ["HIPAA-aligned", "DPDP 2023 ready", "Data residency: India", "256-bit AES at rest", "Audit logs", "Role-based access"],
    faqs: [
      { q: "Is patient data stored in India?", a: "Yes — by default all clinical data is stored in Indian data centres. Enterprise customers can opt for dedicated on-prem servers." },
      { q: "Can I migrate from existing HMS software?", a: "Yes. Our team imports patients, doctors, services and bill formats from common systems (Medixcel, Birlamedisoft, custom Excel) at no cost on Growth+." },
      ...SHARED_FAQ_BILLING,
    ],
    related: ["analytics-dashboards", "ai-chatbots", "automation"],
  },

  /* ------------------------------------------------------------------ */
  {
    slug: "church-management",
    category: "Community",
    brand: "ChurchFlow",
    name: "Church Management Systems",
    tagline: "Members, services, giving and events — all in one calm place.",
    hero: {
      eyebrow: "Faith & Community",
      image:
        "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?auto=format&fit=crop&w=1600&q=80",
      trustBadges: ["Used by 25+ churches", "Multi-language ready", "Mobile + web"],
    },
    outcomes: [
      { value: "3×", label: "Higher member engagement" },
      { value: "60%", label: "Less admin work for pastors" },
      { value: "Free", label: "For congregations under 200" },
    ],
    personas: [
      { name: "Single-Campus Churches", desc: "One building, one service, growing community." },
      { name: "Multi-Campus Churches", desc: "Multiple locations under one umbrella." },
      { name: "Youth Ministries", desc: "Camps, small groups and outreach for young members." },
      { name: "Mission Organisations", desc: "Donor management, trip planning, field reports." },
    ],
    modules: [
      { name: "Member Directory", desc: "Searchable profiles with families, ministries and notes." },
      { name: "Service Planning", desc: "Order of worship, song lists, volunteer rosters." },
      { name: "Online Giving", desc: "Tithes, donations, pledges with 80G / tax receipts." },
      { name: "Events & RSVPs", desc: "Calendars, registrations, paid events." },
      { name: "Small Groups", desc: "Group leaders, attendance, prayer requests." },
      { name: "Bulk SMS / WhatsApp", desc: "Service reminders, prayer chains, announcements." },
      { name: "Member App", desc: "Members get their own iOS/Android app for giving and events." },
      { name: "Reports", desc: "Attendance, giving trends, growth metrics." },
    ],
    packs: [
      { name: "Single-Church Starter", tagline: "Free for churches under 200 members.", features: ["Member directory", "Bulk SMS", "Online giving", "Events"], price: "Free" },
      { name: "Multi-Campus", tagline: "Several locations, one back-office.", features: ["Campus segmentation", "Cross-campus events", "Consolidated giving", "Pastor roles"], price: "₹2,999/mo" },
      { name: "Youth Ministry", tagline: "Engagement-first pack for youth groups.", features: ["Small groups", "Camp registrations", "Mentor pairing", "Devotional plans"], price: "₹999/mo" },
      { name: "Mission Trip Manager", tagline: "Plan, fundraise, run mission trips end-to-end.", features: ["Trip pages", "Fundraising goals", "Volunteer applications", "Field reports"], price: "₹1,499/mo" },
    ],
    howItWorks: ["Create your church workspace", "Pick a pack", "Import members (CSV / contacts)", "Launch your member app"],
    pricing: [
      { tier: "Free", price: "₹0", features: ["Up to 200 members", "Core modules", "Heritex branding on member app"] },
      { tier: "Growth", price: "₹1,499 / mo", popular: true, features: ["Up to 1,000 members", "Custom domain", "Branded member app", "Online giving"] },
      { tier: "Business", price: "₹3,999 / mo", features: ["Unlimited members", "Multi-campus", "Priority support", "Custom reports"] },
      { tier: "Enterprise", price: "Talk to us", features: ["Denomination-wide deployment", "SSO", "Dedicated CSM"] },
    ],
    integrations: ["Razorpay", "Stripe", "WhatsApp", "Twilio", "Mailchimp", "YouTube Live"],
    compliance: SHARED_COMPLIANCE,
    faqs: [
      { q: "Can we accept tithes online?", a: "Yes — integrated with Razorpay & Stripe with automatic tax receipts (80G for Indian churches)." },
      { q: "Will members get their own app?", a: "Yes. Every Growth+ plan includes a branded iOS + Android member app published under your church's name." },
      ...SHARED_FAQ_BILLING,
    ],
    related: ["mobile-apps", "automation", "analytics-dashboards"],
  },

  /* ------------------------------------------------------------------ */
  {
    slug: "school-erp",
    category: "Education",
    brand: null,
    name: "School ERP",
    tagline: "From admissions to alumni — every workflow under one roof.",
    hero: {
      eyebrow: "Education · K-12 & Coaching",
      image:
        "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1600&q=80",
      trustBadges: ["Used by 30+ institutes", "Parent app included", "GST-ready fees"],
    },
    outcomes: [
      { value: "80%", label: "Manual paperwork eliminated" },
      { value: "2×", label: "Faster fee collection" },
      { value: "98%", label: "Parent app adoption rate" },
    ],
    personas: [
      { name: "Pre-schools & Daycare", desc: "Daily reports, photos, fees and pickup management." },
      { name: "K-12 Schools", desc: "Full academic, exam and parent communication suite." },
      { name: "Coaching Institutes", desc: "Batches, tests, attendance and fees automation." },
      { name: "Online Tutoring", desc: "Live classes, recordings, assignments." },
    ],
    modules: [
      { name: "Admissions", desc: "Enquiries, applications, payments, document uploads." },
      { name: "Attendance", desc: "Biometric / app-based, with parent alerts." },
      { name: "Fees & Billing", desc: "Term fees, late fines, GST, online payment." },
      { name: "Exams & Report Cards", desc: "Marks entry, grading schemes, auto-generated PDFs." },
      { name: "Timetable & Substitutions", desc: "Per-class scheduling, teacher load balancing." },
      { name: "Parent / Student App", desc: "Homework, attendance, fees, messaging." },
      { name: "Library & Transport", desc: "Books issued/returned, bus routes, GPS tracking." },
      { name: "Reports", desc: "Academic, financial and operational dashboards." },
    ],
    packs: [
      { name: "Pre-school / Daycare", tagline: "Photos, daily reports, fees — that's it.", features: ["Daily diaries", "Photo sharing", "Pickup tracking", "Fees + reminders"], price: "₹999/mo" },
      { name: "K-12 School", tagline: "Full-stack academic + admin.", features: ["Admissions", "Attendance", "Exams", "Parent app"], price: "₹4,999/mo" },
      { name: "Coaching Institute", tagline: "Batches, tests, follow-ups, fees.", features: ["Batch management", "Test series", "Lead pipeline", "Recurring fees"], price: "₹2,499/mo" },
      { name: "Online Tutoring", tagline: "Live classes + LMS.", features: ["Zoom integration", "Recordings", "Assignments", "Discussion boards"], price: "₹1,999/mo" },
    ],
    howItWorks: ["Sign up", "Pick your education pack", "Import classes / students (Excel)", "Invite teachers & parents"],
    pricing: [
      { tier: "Starter", price: "₹999 / mo", features: ["Up to 50 students", "Core academic modules", "Email support"] },
      { tier: "Growth", price: "₹2,999 / mo", popular: true, features: ["Up to 500 students", "Parent app", "Online fees", "WhatsApp alerts"] },
      { tier: "Business", price: "₹6,999 / mo", features: ["Unlimited students", "Multi-branch", "Custom report cards", "Priority support"] },
      { tier: "Enterprise", price: "Talk to us", features: ["Group of schools / universities", "Dedicated cloud", "Custom modules"] },
    ],
    integrations: ["Razorpay", "WhatsApp", "Zoom", "Google Classroom", "Tally", "Twilio"],
    compliance: SHARED_COMPLIANCE,
    faqs: [
      { q: "Is there a parent app?", a: "Yes — branded iOS + Android parent apps are included on Growth+." },
      { q: "Can we run multiple branches?", a: "Yes — on Business+ each branch has its own data, with consolidated reports for the trust." },
      ...SHARED_FAQ_BILLING,
    ],
    related: ["analytics-dashboards", "automation", "mobile-apps"],
  },

  /* ------------------------------------------------------------------ */
  {
    slug: "ai-chatbots",
    category: "AI",
    brand: "Heritex AI",
    name: "AI Chatbots & Agents",
    tagline: "Always-on assistants for support, sales and lead capture.",
    hero: {
      eyebrow: "AI · Self-serve",
      image:
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1600&q=80",
      trustBadges: ["GPT-5.2 · Claude 4.5 · Gemini 3", "Trained on your data", "WhatsApp + Web"],
    },
    outcomes: [
      { value: "24/7", label: "Always-on assistant" },
      { value: "40 hrs", label: "Saved per week per team" },
      { value: "5 min", label: "From signup to live bot" },
    ],
    personas: [
      { name: "Lead Capture", desc: "Qualify visitors and book demos automatically." },
      { name: "Customer Support", desc: "Tier-1 answers, deflect 60-80% of tickets." },
      { name: "E-commerce Concierge", desc: "Product discovery, sizing, post-purchase help." },
      { name: "Internal Knowledge", desc: "HR / Ops / IT FAQ bot for your team." },
    ],
    modules: [
      { name: "Knowledge Base (RAG)", desc: "Upload PDFs, URLs, docs — the bot grounds answers in your data." },
      { name: "WhatsApp Channel", desc: "Connect a WhatsApp Business number in minutes." },
      { name: "Web Widget", desc: "Drop-in chat for your website, customisable colours & avatar." },
      { name: "Workflows & Handoff", desc: "Capture leads, book calendar slots, hand off to a human." },
      { name: "Voice Agents", desc: "Inbound / outbound calling powered by realtime voice models." },
      { name: "Analytics", desc: "Conversations, deflection, CSAT, top intents." },
    ],
    packs: [
      { name: "Lead Capture Bot", tagline: "Turn website traffic into qualified pipeline.", features: ["Visitor qualification", "Calendar booking", "CRM push", "WhatsApp follow-up"], price: "Free" },
      { name: "Customer Support Bot", tagline: "Deflect tickets, escalate the rest.", features: ["Doc-grounded answers", "Sentiment routing", "Human handoff", "CSAT scoring"], price: "₹2,499/mo" },
      { name: "E-com Concierge", tagline: "Product discovery & post-purchase help.", features: ["Catalogue grounding", "Order status lookup", "Returns flow", "Upsell prompts"], price: "₹2,999/mo" },
      { name: "Appointment Bot", tagline: "For clinics, salons, consultants.", features: ["Service catalogue", "Slot booking", "Payments", "Reschedule / cancel"], price: "₹1,499/mo" },
      { name: "Real Estate Bot", tagline: "Pre-qualify buyers, share listings.", features: ["Listing search", "Site-visit booking", "Broker handoff", "Document collection"], price: "₹1,999/mo" },
      { name: "HR / Internal FAQ Bot", tagline: "Policies, leave, payroll — answered.", features: ["SSO login", "Policy grounding", "Slack / Teams", "Ticket fallback"], price: "₹3,999/mo" },
    ],
    howItWorks: ["Sign up", "Pick a bot pack", "Upload your docs / connect catalogue", "Embed widget / connect WhatsApp", "Watch conversations roll in"],
    pricing: [
      { tier: "Free", price: "₹0", features: ["1 bot", "500 messages / mo", "Heritex branding", "Web widget"] },
      { tier: "Growth", price: "₹1,999 / mo", popular: true, features: ["3 bots", "10,000 messages / mo", "WhatsApp channel", "CRM integrations"] },
      { tier: "Business", price: "₹5,999 / mo", features: ["Unlimited bots", "100k messages / mo", "Voice agents", "Priority support"] },
      { tier: "Enterprise", price: "Talk to us", features: ["Dedicated capacity", "Custom models", "On-prem deployment", "SLA"] },
    ],
    integrations: ["WhatsApp Business", "Stripe", "Razorpay", "HubSpot", "Slack", "Zendesk"],
    compliance: SHARED_COMPLIANCE,
    faqs: [
      { q: "Which LLM does it use?", a: "We auto-route between GPT-5.2, Claude Sonnet 4.5 and Gemini 3 based on the task — for accuracy + cost. Enterprise can pin a single model." },
      { q: "Will it hallucinate?", a: "Every answer is grounded in your uploaded knowledge with citations. Bot refuses to answer outside that scope by default." },
      ...SHARED_FAQ_BILLING,
    ],
    related: ["automation", "custom-crm", "analytics-dashboards"],
  },

  /* ------------------------------------------------------------------ */
  {
    slug: "mobile-apps",
    category: "Mobile",
    brand: null,
    name: "Mobile Apps",
    tagline: "Native-feel iOS & Android products — shipped fast.",
    hero: {
      eyebrow: "Mobile · Custom + Templates",
      image:
        "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1600&q=80",
      trustBadges: ["iOS + Android from one codebase", "Push, payments & maps included", "App-store submission handled"],
    },
    outcomes: [
      { value: "4-8 wks", label: "Idea → store-ready build" },
      { value: "1", label: "Codebase. Two stores." },
      { value: "100%", label: "Owned by you" },
    ],
    personas: [
      { name: "Startup Founders", desc: "Idea-stage MVPs to attract users and investors." },
      { name: "Growing Businesses", desc: "Branded customer or staff apps to lock-in loyalty." },
      { name: "Marketplaces", desc: "Two-sided apps (buyer + seller / customer + agent)." },
      { name: "Community Builders", desc: "Members-only experiences with feeds, events, chat." },
    ],
    modules: [
      { name: "Auth & Profiles", desc: "Email, OTP, Google, Apple — production-ready." },
      { name: "Push Notifications", desc: "Targeted, scheduled, transactional." },
      { name: "Payments", desc: "UPI, cards, wallets via Stripe / Razorpay." },
      { name: "Maps & Location", desc: "Live tracking, geofencing, delivery routing." },
      { name: "Offline Mode", desc: "Local cache + sync for poor-connectivity scenarios." },
      { name: "Analytics & Crash", desc: "Funnel tracking, performance, crash reports." },
    ],
    packs: [
      { name: "Delivery App", tagline: "Customer + driver + admin in one platform.", features: ["Live tracking", "OTP delivery", "Driver app", "Admin dashboard"], price: "From ₹2,49,000 one-time" },
      { name: "Booking App", tagline: "Salons, clinics, fitness, consultations.", features: ["Service catalogue", "Slot booking", "Payments", "Reminders"], price: "From ₹1,49,000 one-time" },
      { name: "Community App", tagline: "Members-only feed, events, chat.", features: ["Feed & posts", "Events & RSVPs", "Direct messages", "Subscriptions"], price: "From ₹1,99,000 one-time" },
      { name: "Loyalty App", tagline: "Branded loyalty for retail / F&B.", features: ["Points & rewards", "Coupons", "Push offers", "POS integration"], price: "From ₹99,000 one-time" },
      { name: "Mini Marketplace", tagline: "Two-sided commerce app.", features: ["Buyer & seller flows", "Listings & search", "In-app payments", "Reviews"], price: "From ₹2,99,000 one-time" },
      { name: "Custom App", tagline: "Anything else — we'll architect & build it.", features: ["Discovery workshop", "Design system", "Engineering", "Store submission"], price: "Quote" },
    ],
    howItWorks: ["Pick a template or share your idea", "Design sprint (1-2 weeks)", "Engineering sprints (3-6 weeks)", "Beta on TestFlight + Play Console", "Store launch"],
    pricing: [
      { tier: "Template Build", price: "From ₹99,000", features: ["Pick a pack", "Branded for you", "iOS + Android", "App-store submission"] },
      { tier: "Custom Build", price: "From ₹4,99,000", popular: true, features: ["Custom UX & flows", "Discovery + design", "Engineering sprints", "Launch support"] },
      { tier: "Build + Maintain", price: "From ₹49,000 / mo", features: ["Bug fixes & updates", "OS upgrades", "Analytics review", "Quarterly roadmap"] },
      { tier: "Enterprise", price: "Talk to us", features: ["Dedicated team", "On-prem APIs", "Security review", "SLA"] },
    ],
    integrations: ["Stripe", "Razorpay", "Firebase", "OneSignal", "Mapbox", "Mixpanel"],
    compliance: SHARED_COMPLIANCE,
    faqs: [
      { q: "Do I own the source code?", a: "Yes — on every plan. Code is handed over with full documentation at launch." },
      { q: "Native or cross-platform?", a: "We use React Native for production speed and native-feel performance. We've yet to hit a wall it couldn't handle." },
      ...SHARED_FAQ_BILLING,
    ],
    related: ["business-websites", "e-commerce", "ai-chatbots"],
  },

  /* ------------------------------------------------------------------ */
  {
    slug: "business-websites",
    category: "Web",
    brand: null,
    name: "Business Websites",
    tagline: "Marketing sites that turn visitors into qualified pipeline.",
    hero: {
      eyebrow: "Web · Self-serve + Custom",
      image:
        "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?auto=format&fit=crop&w=1600&q=80",
      trustBadges: ["Lighthouse 95+ scores", "SEO-ready out of the box", "Live in 5 days"],
    },
    outcomes: [
      { value: "5 days", label: "From kickoff to live site" },
      { value: "2.4×", label: "Avg. inbound lead lift" },
      { value: "95+", label: "Lighthouse perf score" },
    ],
    personas: [
      { name: "Service Businesses", desc: "Doctors, salons, agencies, consultants." },
      { name: "Restaurants & Hospitality", desc: "Menus, reservations, location pages." },
      { name: "Real Estate", desc: "Property listings, project pages, lead capture." },
      { name: "Personal Brands", desc: "Coaches, creators, speakers, authors." },
    ],
    modules: [
      { name: "CMS", desc: "Edit content without touching code. Roles + previews." },
      { name: "SEO Toolkit", desc: "Meta, schema, sitemap, robots — all handled." },
      { name: "Forms & Lead Capture", desc: "Pipe leads to CRM, email, WhatsApp." },
      { name: "Blog / Journal", desc: "Editorial-grade publishing with categories & RSS." },
      { name: "Analytics", desc: "Privacy-friendly tracking + heat maps." },
      { name: "A/B Testing", desc: "Hero, CTA and form variants — measure what wins." },
    ],
    packs: [
      { name: "Agency / Studio", tagline: "Editorial portfolio with case studies.", features: ["Case-study CMS", "Editorial typography", "Lead form", "Blog"], price: "From ₹49,000" },
      { name: "Restaurant", tagline: "Menu, reservations, location pages.", features: ["Multi-location", "Menu CMS", "Reservations", "WhatsApp orders"], price: "From ₹39,000" },
      { name: "Salon & Spa", tagline: "Services, pricing, online booking.", features: ["Online booking", "Service catalogue", "Stylist profiles", "Reviews"], price: "From ₹39,000" },
      { name: "Doctor / Clinic", tagline: "Services, doctor profiles, appointments.", features: ["Doctor profiles", "Appointments", "Insurance accepted", "Reviews"], price: "From ₹49,000" },
      { name: "Real Estate Listings", tagline: "Projects, units, lead capture.", features: ["Project pages", "Unit configurator", "Site-visit form", "Broker portal"], price: "From ₹79,000" },
      { name: "Personal Brand", tagline: "Coaches, creators, speakers.", features: ["Editorial home", "Newsletter signup", "Course / book CTA", "Press logos"], price: "From ₹29,000" },
    ],
    howItWorks: ["Pick a website pack", "Share copy + brand assets (or use AI draft)", "We design + build", "You review + we launch on your domain"],
    pricing: [
      { tier: "Starter", price: "From ₹29,000 one-time", features: ["1-pack template", "5 pages", "Custom domain", "Mobile-responsive"] },
      { tier: "Growth", price: "From ₹49,000 one-time", popular: true, features: ["Custom design", "Up to 12 pages", "CMS + Blog", "Forms + CRM"] },
      { tier: "Business", price: "From ₹99,000 one-time", features: ["Bespoke design system", "Up to 25 pages", "A/B testing", "Quarterly optimisation"] },
      { tier: "Care Plan", price: "₹4,999 / mo", features: ["Hosting + monitoring", "Backups", "Monthly edits", "Performance audits"] },
    ],
    integrations: ["HubSpot", "Mailchimp", "Razorpay", "Google Analytics", "Plausible", "Calendly"],
    compliance: SHARED_COMPLIANCE,
    faqs: [
      { q: "Will my site rank on Google?", a: "Sites ship SEO-ready: schema, sitemap, robots, meta, og-images. We can also offer monthly SEO retainers." },
      { q: "Can I edit content myself?", a: "Yes — every site comes with a friendly CMS for marketers (no code)." },
      ...SHARED_FAQ_BILLING,
    ],
    related: ["e-commerce", "automation", "analytics-dashboards"],
  },

  /* ------------------------------------------------------------------ */
  {
    slug: "e-commerce",
    category: "Commerce",
    brand: null,
    name: "E-Commerce Platforms",
    tagline: "Premium storefronts and marketplaces that actually convert.",
    hero: {
      eyebrow: "Commerce · Self-serve + Custom",
      image:
        "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=1600&q=80",
      trustBadges: ["UPI + Cards + COD", "Mobile-first checkout", "Built-in SEO"],
    },
    outcomes: [
      { value: "2.4×", label: "Avg. mobile conversion lift" },
      { value: "<1s", label: "Storefront page-load" },
      { value: "Day 1", label: "Live with payments & shipping" },
    ],
    personas: [
      { name: "D2C Brands", desc: "Single-vendor stores with subscriptions and bundles." },
      { name: "Marketplaces", desc: "Multi-vendor commerce with seller onboarding." },
      { name: "Restaurants", desc: "Online ordering with delivery / pickup / WhatsApp." },
      { name: "B2B Wholesale", desc: "Price tiers, quote requests, GST invoices." },
    ],
    modules: [
      { name: "Catalogue & Variants", desc: "Unlimited products, variants, bundles, gift cards." },
      { name: "Cart & Checkout", desc: "1-tap UPI, cards, wallets, COD, abandoned cart." },
      { name: "Subscriptions", desc: "Weekly, monthly, custom intervals with pause / skip." },
      { name: "Shipping", desc: "Shiprocket / Delhivery / custom couriers." },
      { name: "Coupons & Loyalty", desc: "Codes, gifts, referrals, points." },
      { name: "Admin Dashboard", desc: "Orders, inventory, customers, reports." },
    ],
    packs: [
      { name: "Single-Vendor Store", tagline: "Classic D2C brand storefront.", features: ["Catalogue", "Checkout", "Payments + COD", "Shiprocket"], price: "From ₹49,000" },
      { name: "Multi-Vendor Marketplace", tagline: "Onboard sellers, take commission.", features: ["Seller portal", "Commission engine", "Payouts", "KYC + GST"], price: "From ₹2,99,000" },
      { name: "Subscription Box", tagline: "Recurring revenue D2C.", features: ["Box builder", "Recurring billing", "Pause / skip", "Gift subscriptions"], price: "From ₹99,000" },
      { name: "Restaurant Online Ordering", tagline: "Delivery, pickup, WhatsApp.", features: ["Menu CMS", "Modifiers", "Delivery zones", "WhatsApp orders"], price: "From ₹49,000" },
      { name: "B2B Wholesale", tagline: "Price tiers + quote-based ordering.", features: ["Customer-specific pricing", "Quote requests", "Credit limits", "GST invoices"], price: "From ₹1,49,000" },
      { name: "Digital Products", tagline: "Sell PDFs, courses, downloads.", features: ["Secure delivery", "License keys", "Watermarking", "Drip access"], price: "From ₹39,000" },
    ],
    howItWorks: ["Pick a commerce pack", "Upload catalogue (CSV / Shopify export)", "Connect payments + shipping", "Launch on your domain"],
    pricing: [
      { tier: "Starter", price: "From ₹39,000 one-time", features: ["Up to 100 SKUs", "Hosted storefront", "Payments + COD"] },
      { tier: "Growth", price: "From ₹99,000 one-time", popular: true, features: ["Unlimited SKUs", "Subscriptions", "Custom design", "Mobile app add-on"] },
      { tier: "Business", price: "From ₹2,99,000 one-time", features: ["Multi-vendor / B2B", "Advanced reports", "ERP integration", "Priority support"] },
      { tier: "Care + Growth", price: "From ₹9,999 / mo", features: ["Hosting + monitoring", "Conversion optimisation", "A/B tests", "Monthly roadmap"] },
    ],
    integrations: ["Razorpay", "Stripe", "Shiprocket", "Delhivery", "Tally", "WhatsApp"],
    compliance: SHARED_COMPLIANCE,
    faqs: [
      { q: "Can I migrate from Shopify / WooCommerce?", a: "Yes — we import products, customers, orders and redirects. Migrations are free on Growth+." },
      { q: "Do I get a mobile app?", a: "Storefront is fully mobile-responsive. A native iOS / Android app is available as an add-on on Growth+." },
      ...SHARED_FAQ_BILLING,
    ],
    related: ["mobile-apps", "business-websites", "automation"],
  },

  /* ------------------------------------------------------------------ */
  {
    slug: "automation",
    category: "Automation",
    brand: null,
    name: "Automation Solutions",
    tagline: "Kill repetitive work. Connect every tool you already use.",
    hero: {
      eyebrow: "Automation · Self-serve",
      image:
        "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1600&q=80",
      trustBadges: ["No-code workflows", "100+ app connectors", "AI-powered steps"],
    },
    outcomes: [
      { value: "40 hrs", label: "Avg. weekly hours saved" },
      { value: "100+", label: "App connectors" },
      { value: "0", label: "Lines of code required" },
    ],
    personas: [
      { name: "Founders & Ops", desc: "Automate everything that doesn't deserve a human." },
      { name: "Sales Teams", desc: "Lead routing, follow-ups, hand-offs." },
      { name: "HR Teams", desc: "Onboarding, payroll, leave approvals." },
      { name: "Marketing Teams", desc: "Lead nurture, social, reporting." },
    ],
    modules: [
      { name: "Visual Workflow Builder", desc: "Trigger → conditions → actions, drag-and-drop." },
      { name: "AI Steps", desc: "Summarise, classify, extract, generate — inside any workflow." },
      { name: "Schedules & Triggers", desc: "Time-based, webhook, form-fill, app events." },
      { name: "Error Handling", desc: "Retries, fallbacks, alerts when something breaks." },
      { name: "Audit Logs", desc: "Every run, every step, every input — searchable." },
      { name: "Multi-workspace", desc: "Separate workflows per team or client." },
    ],
    packs: [
      { name: "Lead Routing", tagline: "Form fill → enrich → assign → notify.", features: ["Form ingestion", "Lead enrichment", "Round-robin assignment", "Slack / WhatsApp alerts"], price: "Free" },
      { name: "Invoice Automation", tagline: "Quote → invoice → reminder → reconcile.", features: ["Invoice generator", "Payment links", "Auto reminders", "Tally / Zoho sync"], price: "₹1,499/mo" },
      { name: "HR Onboarding", tagline: "Offer → docs → accounts → welcome.", features: ["Offer letter", "Doc collection", "Google / Slack provisioning", "Day-1 checklist"], price: "₹1,999/mo" },
      { name: "Social Scheduler", tagline: "Plan, queue, post — across networks.", features: ["Multi-channel posting", "AI captions", "Calendar view", "Analytics"], price: "₹999/mo" },
      { name: "WhatsApp Drip", tagline: "Nurture leads or customers on WhatsApp.", features: ["Sequence builder", "Personalisation", "Opt-out handling", "Reporting"], price: "₹1,499/mo" },
      { name: "Email Sequences", tagline: "Behavioural email + drip campaigns.", features: ["Visual builder", "Branching logic", "A/B testing", "Inbox warmup"], price: "₹999/mo" },
    ],
    howItWorks: ["Sign up", "Pick a workflow pack", "Connect your apps (OAuth)", "Turn it on"],
    pricing: [
      { tier: "Free", price: "₹0", features: ["100 runs / mo", "3 workflows", "Heritex branding"] },
      { tier: "Growth", price: "₹999 / mo", popular: true, features: ["5,000 runs / mo", "Unlimited workflows", "AI steps", "All connectors"] },
      { tier: "Business", price: "₹3,999 / mo", features: ["50,000 runs / mo", "Custom code steps", "Priority support", "Team workspaces"] },
      { tier: "Enterprise", price: "Talk to us", features: ["Dedicated runs", "SSO / SAML", "On-prem agents", "SLA"] },
    ],
    integrations: ["WhatsApp", "Slack", "Gmail", "Google Sheets", "Notion", "Tally"],
    compliance: SHARED_COMPLIANCE,
    faqs: [
      { q: "Do I need to know code?", a: "No — workflows are visual. Code steps are optional and only for advanced users." },
      { q: "Can it call AI models inside a workflow?", a: "Yes — drop an AI step anywhere to summarise, classify, extract or generate. Powered by GPT-5.2 / Claude 4.5 / Gemini 3." },
      ...SHARED_FAQ_BILLING,
    ],
    related: ["ai-chatbots", "custom-crm", "analytics-dashboards"],
  },

  /* ------------------------------------------------------------------ */
  {
    slug: "analytics-dashboards",
    category: "Analytics",
    brand: null,
    name: "Analytics Dashboards",
    tagline: "See revenue, ops and product health in one calm, real-time view.",
    hero: {
      eyebrow: "Analytics · Self-serve",
      image:
        "https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=1600&q=80",
      trustBadges: ["100+ data connectors", "AI insights", "Real-time refresh"],
    },
    outcomes: [
      { value: "1 view", label: "Across every tool" },
      { value: "Real-time", label: "Refresh, not weekly emails" },
      { value: "AI", label: "Anomaly detection built-in" },
    ],
    personas: [
      { name: "Founders / CEOs", desc: "One screen for revenue, growth and runway." },
      { name: "Sales Leaders", desc: "Pipeline, forecast and rep performance." },
      { name: "Marketing", desc: "Channel ROI, funnel, attribution." },
      { name: "Operations", desc: "Inventory, SLAs, ops bottlenecks." },
    ],
    modules: [
      { name: "Data Connectors", desc: "Stripe, Razorpay, GA, ad platforms, Sheets, CRMs, MySQL/Postgres." },
      { name: "Visual Builder", desc: "Drag tiles, charts, tables — no SQL needed." },
      { name: "AI Insights", desc: "Plain-English summaries + anomaly alerts." },
      { name: "Scheduled Reports", desc: "Daily / weekly PDFs to email or WhatsApp." },
      { name: "Permissions", desc: "Workspace, team and per-dashboard roles." },
      { name: "Embed & Share", desc: "Public links, password-protected, or embed in your app." },
    ],
    packs: [
      { name: "Sales Dashboard", tagline: "Pipeline + revenue + forecast.", features: ["Pipeline funnel", "Rep leaderboards", "Forecasts", "Win/loss analysis"], price: "₹999/mo" },
      { name: "Marketing Dashboard", tagline: "Channels, funnel, attribution.", features: ["Channel ROI", "Funnel analysis", "Multi-touch attribution", "Cohort retention"], price: "₹999/mo" },
      { name: "Operations Dashboard", tagline: "Inventory, SLAs, bottlenecks.", features: ["SLA tracking", "Inventory health", "Throughput", "Issue heatmap"], price: "₹1,499/mo" },
      { name: "Finance Dashboard", tagline: "MRR, burn, runway, ageing.", features: ["MRR / ARR", "Burn & runway", "Ageing report", "Cash forecast"], price: "₹1,499/mo" },
      { name: "E-commerce Dashboard", tagline: "Orders, AOV, RoAS, repeat rate.", features: ["AOV & RoAS", "Repeat rate", "Top SKUs", "Inventory alerts"], price: "₹999/mo" },
      { name: "Customer Support Dashboard", tagline: "Tickets, CSAT, deflection.", features: ["Volume & SLA", "CSAT trends", "Bot deflection", "Agent leaderboards"], price: "₹999/mo" },
    ],
    howItWorks: ["Sign up", "Pick a dashboard pack", "Connect your data (OAuth or DB)", "Get insights via email / WhatsApp"],
    pricing: [
      { tier: "Free", price: "₹0", features: ["1 dashboard", "3 connectors", "Heritex branding"] },
      { tier: "Growth", price: "₹999 / mo", popular: true, features: ["Unlimited dashboards", "All connectors", "AI insights", "Scheduled reports"] },
      { tier: "Business", price: "₹3,999 / mo", features: ["Embed in your app", "Custom domain", "Priority support", "Team workspaces"] },
      { tier: "Enterprise", price: "Talk to us", features: ["On-prem warehouse", "SSO", "SLA", "Dedicated CSM"] },
    ],
    integrations: ["Stripe", "Razorpay", "Google Analytics", "Meta Ads", "Google Sheets", "PostgreSQL"],
    compliance: SHARED_COMPLIANCE,
    faqs: [
      { q: "Do I need SQL?", a: "No — the builder is fully visual. For advanced users, a SQL editor is available on Growth+." },
      { q: "Is my raw data stored?", a: "By default we cache only aggregated metrics. Enterprise customers can route to their own warehouse with zero data leaving their VPC." },
      ...SHARED_FAQ_BILLING,
    ],
    related: ["automation", "custom-crm", "ai-chatbots"],
  },
];

export const productsBySlug = Object.fromEntries(products.map((p) => [p.slug, p]));
export default products;
